import { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import useAppointments from "../useAppointments";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import { socketConnection } from "../../services/socket";

const useAppointmentsData = () => {
  const { user } = useContext(AuthContext);
  const { list, remove } = useAppointments();

  const isAdmin = user.profile === "admin" || user.profile === "supervisor";

  const [appointments, setAppointments] = useState([]);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState(isAdmin ? "" : user.id);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [initialStart, setInitialStart] = useState(null);
  const [deletingAppointment, setDeletingAppointment] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await list(userFilter ? { userId: userFilter } : {});
      setAppointments(data);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilter]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/users/list");
        setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [isAdmin]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-appointment`, () => {
      loadAppointments();
    });

    return () => {
      socket.disconnect();
    };
  }, [loadAppointments]);

  const openNew = () => {
    setSelectedAppointment(null);
    setInitialStart(null);
    setModalOpen(true);
  };

  const openNewAt = (date) => {
    setSelectedAppointment(null);
    setInitialStart(date);
    setModalOpen(true);
  };

  const openEdit = (appointment) => {
    setSelectedAppointment(appointment);
    setInitialStart(null);
    setModalOpen(true);
  };

  const requestDelete = (appointment) => {
    setDeletingAppointment(appointment);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await remove(deletingAppointment.id);
      toast.success("Cita eliminada.");
      loadAppointments();
    } catch (err) {
      toastError(err);
    }
    setDeletingAppointment(null);
  };

  return {
    currentUser: user,
    isAdmin,
    appointments,
    users,
    userFilter,
    setUserFilter,
    loading,
    modalOpen,
    setModalOpen,
    selectedAppointment,
    initialStart,
    openNew,
    openNewAt,
    openEdit,
    confirmOpen,
    setConfirmOpen,
    requestDelete,
    confirmDelete,
    loadAppointments,
  };
};

export default useAppointmentsData;
